import React, { useState } from 'react';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { getBenefitType } from '../../PaymentPlans/utils';
import { ItemSelect } from './ItemSelect';
import { JdsButton } from '../../../components/jds/JdsButton';
import { JdsTag } from '../../../components/jds/JdsTag';
import { JdsCollapse } from '../../../components/jds/JdsCollapse';
import {
  TweenedCurrency,
  TweenedInteger,
} from '../../../components/jds/TweenedCurrency';

export const ITEM_TYPE_ADDON = 'ADDON';

// TODO: substituir pelas opções reais de tipo de item quando definidas.
export const ITEM_TYPE_OPTIONS = [{ value: ITEM_TYPE_ADDON, label: 'Produto' }];

export function getItemPrice(item) {
  return Number(item?.value ?? item?.price ?? item?.amount ?? 0);
}

// planItem vem do plano selecionado (plan.planItems); addonQuantity é só a
// parte controlada nesta linha — a quantidade do plano nunca é somada ao payload,
// só exibida aqui para deixar a soma visível.
function getPlanOverlap(planItem, addonQuantity) {
  const type = getBenefitType(planItem);
  const unitSuffix = planItem.item?.unit ? ` ${planItem.item.unit}` : '';
  if (type === 'CREDITS' && planItem.itemTotal != null) {
    return {
      planQuantity: planItem.itemTotal,
      addonQuantity,
      total: planItem.itemTotal + addonQuantity,
      unitSuffix,
    };
  }
  return { total: null };
}

const quantityButtonStyle = {
  width: '40px',
  border: 'none',
  background: 'transparent',
  fontSize: 'var(--fs-18)',
  lineHeight: 1,
  color: 'var(--fg-neutral-default)',
};

const quantityInputStyle = {
  width: '48px',
  border: 'none',
  background: 'transparent',
  textAlign: 'center',
  padding: 0,
  outline: 'none',
};

export function OfferItemRow({
  item,
  planItem,
  discount,
  selectedIds,
  error,
  onChange,
  onRemove,
}) {
  // Texto digitado enquanto o campo está em edição (permite apagar e redigitar).
  const [quantityDraft, setQuantityDraft] = useState(null);
  const selectedItem = item.item || null;
  const unitPrice = getItemPrice(selectedItem);
  const quantityDisabled = !selectedItem;
  const planOverlap =
    selectedItem && planItem ? getPlanOverlap(planItem, item.quantity) : null;

  return (
    <div
      id={`offer-item-${item.key}`}
      className={`jds-surface${item._flash ? ' jds-flash' : ''}`}
      style={{
        borderLeft: planOverlap
          ? '4px solid var(--border-brand-default)'
          : '4px solid transparent',
        transition: 'border-color var(--dur-base) var(--ease-out)',
      }}
    >
      <div className="d-flex align-items-start" style={{ gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div className="form-group row" style={{ marginBottom: 0 }}>
            <div className="col-lg-5">
              <label>Produto</label>
              <ItemSelect
                value={selectedItem}
                selectedIds={selectedIds}
                onChange={(selected) =>
                  onChange({
                    item: selected,
                    itemId: selected ? selected.id : '',
                  })
                }
              />
              <JdsCollapse in={!!error}>
                <div className="text-danger">{error}</div>
              </JdsCollapse>
              <JdsCollapse in={!!selectedItem}>
                <small className="text-muted d-block mt-1">
                  {formatCurrency(unitPrice)} / unidade
                </small>
              </JdsCollapse>
            </div>
            <div className="col-lg-3">
              <label>Quantidade</label>
              <div
                className="form-control d-flex align-items-center p-0"
                style={{
                  backgroundColor: quantityDisabled
                    ? 'var(--state-disabled-outlined-bg)'
                    : 'var(--bg-neutral-layer01)',
                  cursor: quantityDisabled ? 'not-allowed' : 'default',
                }}
              >
                <button
                  type="button"
                  className="jds-qty-btn"
                  aria-label="Diminuir quantidade"
                  style={quantityButtonStyle}
                  disabled={quantityDisabled || item.quantity <= 1}
                  onClick={() => onChange({ quantity: item.quantity - 1 })}
                >
                  −
                </button>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center text-nowrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Quantidade"
                    style={quantityInputStyle}
                    disabled={quantityDisabled}
                    value={quantityDraft ?? item.quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setQuantityDraft(value);
                      const parsed = parseInt(value, 10);
                      if (parsed >= 1) onChange({ quantity: parsed });
                    }}
                    onBlur={() => setQuantityDraft(null)}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <button
                  type="button"
                  className="jds-qty-btn"
                  aria-label="Aumentar quantidade"
                  style={quantityButtonStyle}
                  disabled={quantityDisabled}
                  onClick={() => onChange({ quantity: item.quantity + 1 })}
                >
                  +
                </button>
              </div>
            </div>
            <div className="col-lg-4">
              <label className="d-block text-right">Total</label>
              <div
                className="d-flex flex-column align-items-end justify-content-center"
                style={{ height: 'calc(1.5em + 1.3rem + 2px)' }}
              >
                {discount ? (
                  // key: troca preço simples <-> riscado+desconto com fade
                  <React.Fragment key="discount">
                    <span
                      className="text-muted jds-fade"
                      style={{ textDecoration: 'line-through' }}
                    >
                      <TweenedCurrency value={discount.original} />
                    </span>
                    <span
                      className="jds-title-sm"
                      style={{ color: 'var(--fg-brand-strong)' }}
                    >
                      <TweenedCurrency value={discount.discounted} />
                    </span>
                  </React.Fragment>
                ) : (
                  <span key="plain" className="jds-title-sm jds-fade">
                    <TweenedCurrency value={unitPrice * item.quantity} />
                  </span>
                )}
              </div>
            </div>
          </div>

          <JdsCollapse in={!!planOverlap}>
            {planOverlap && (
            <div
              className="d-flex align-items-center flex-wrap mt-3"
              style={{ gap: '8px' }}
            >
              {planOverlap.total != null ? (
                <>
                  <JdsTag
                    color="brand"
                    label={`Plano: ${planOverlap.planQuantity}${planOverlap.unitSuffix}`}
                  />
                  <span className="text-muted">+</span>
                  <JdsTag
                    color="brand"
                    label={`Produto: ${planOverlap.addonQuantity}${planOverlap.unitSuffix}`}
                  />
                  <span className="text-muted">=</span>
                  <JdsTag
                    color="success"
                    dot
                    label={
                      <>
                        <TweenedInteger value={planOverlap.total} />
                        {planOverlap.unitSuffix} disponíveis ao cliente
                      </>
                    }
                  />
                </>
              ) : (
                <span className="text-muted">
                  Já incluso no plano selecionado (acesso ilimitado) — este
                  produto pode ser redundante, mas não será bloqueado.
                </span>
              )}
            </div>
            )}
          </JdsCollapse>
        </div>

        <JdsButton
          variant="ghost"
          color="error"
          iconOnly
          style={{ marginTop: 'var(--space-24)' }}
          title="Remover item"
          aria-label="Remover item"
          onClick={onRemove}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v5M14 11v5" />
          </svg>
        </JdsButton>
      </div>
    </div>
  );
}
